import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, CalendarClock, UserPlus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { cn, getInitials } from '@/lib/utils';
import {
  useSessionAttendance,
  useMarkAttendance,
  useMarkAllPresent,
  useCancelMakeup,
  useAssignedPlayers,
  useAddPlayersToSession,
} from '@/hooks/use-attendance';
import { SessionNotes } from '@/components/coach/session-notes';
import { ClinicTopic } from './clinic-topic';

// ─── Add Players Sheet ────────────────────────────────────────────────────────

function AddPlayersSheet({
  sessionId,
  open,
  onOpenChange,
}: {
  sessionId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: available } = useAssignedPlayers(sessionId);
  const addPlayers = useAddPlayersToSession(sessionId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return available.filter((p) =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q)
    );
  }, [available, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await addPlayers.mutate(Array.from(selected));
      setSelected(new Set());
      setSearch('');
      onOpenChange(false);
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSelected(new Set());
    setSearch('');
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add Players
          </SheetTitle>
        </SheetHeader>

        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">
              {available.length === 0
                ? 'All assigned players are already in this session'
                : 'No players match your search'}
            </p>
          ) : (
            <div className="space-y-1">
              {filtered.map((player) => {
                const isSelected = selected.has(player.id);
                return (
                  <div
                    key={player.id}
                    onClick={() => toggle(player.id)}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted border border-transparent'
                    )}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {getInitials(player.firstName, player.lastName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">
                        {player.firstName} {player.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {player.level || 'Unassigned'}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-4 border-t">
          <Button
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
            className="w-full"
          >
            {adding
              ? 'Adding...'
              : selected.size === 0
              ? 'Select players to add'
              : `Add ${selected.size} player${selected.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Clinic Session View ──────────────────────────────────────────────────────

interface ClinicSessionViewProps {
  sessionId: string;
}

export function ClinicSessionView({ sessionId }: ClinicSessionViewProps) {
  const navigate = useNavigate();
  const { data } = useSessionAttendance(sessionId);
  const markAttendance = useMarkAttendance(sessionId);
  const markAllPresent = useMarkAllPresent(sessionId);
  const cancelMakeup = useCancelMakeup(sessionId);
  const [addSheetOpen, setAddSheetOpen] = useState(false);

  const handleToggleAttendance = (playerId: string, currentStatus: string) => {
    let newStatus: 'present' | 'late' | 'absent';
    if (currentStatus === 'present') newStatus = 'late';
    else if (currentStatus === 'late') newStatus = 'absent';
    else newStatus = 'present';
    markAttendance.mutate({ playerId, status: newStatus });
  };

  const handleRequestMakeup = (playerId: string, hasMakeupRequest: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMakeupRequest) {
      cancelMakeup.mutate({ playerId });
    } else {
      markAttendance.mutate({ playerId, status: 'absent', makeupRequired: true });
    }
  };

  const handleMarkAllPresent = () => {
    if (!data) return;
    const notPresentIds = data.players
      .filter(p => p.status !== 'present')
      .map(p => p.id);
    if (notPresentIds.length > 0) {
      markAllPresent.mutate(notPresentIds);
    }
  };

  if (!data) return null;

  return (
    <>
      {/* Clinic Topic */}
      <ClinicTopic sessionId={sessionId} initialTopic={data.session.clinicTopic} />

      {/* Player List — no eval buttons */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Players
              </CardTitle>
              <CardDescription>Tap a player to cycle their status</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddSheetOpen(true)}
              className="gap-1.5"
            >
              <UserPlus className="w-4 h-4" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.players.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-3">
                No players added to this session yet
              </p>
              <Button size="sm" onClick={() => setAddSheetOpen(true)} className="gap-1.5">
                <UserPlus className="w-4 h-4" />
                Add Players
              </Button>
            </div>
          ) : (
            data.players.map(player => (
              <div
                key={player.id}
                onClick={() => handleToggleAttendance(player.id, player.status)}
                className={cn(
                  'flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition',
                  player.status === 'present'
                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                    : player.status === 'late'
                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30'
                    : player.status === 'absent'
                    ? 'border-red-300 bg-red-50 dark:bg-red-950/30'
                    : 'border-border bg-card hover:bg-muted/40'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-sm">
                    {getInitials(player.firstName, player.lastName)}
                  </div>
                  <div>
                    <div className="font-medium text-foreground">
                      {player.firstName} {player.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {player.level || 'Unassigned'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {player.status === 'absent' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={cn(
                        'h-8 px-2',
                        player.hasMakeupRequest
                          ? 'border-green-500 text-green-600 hover:bg-green-50'
                          : 'border-orange-400 text-orange-600 hover:bg-orange-50'
                      )}
                      onClick={(e) => handleRequestMakeup(player.id, player.hasMakeupRequest, e)}
                    >
                      {player.hasMakeupRequest ? (
                        <><CheckCircle className="w-4 h-4 mr-1" /><span className="text-xs">Makeup Requested</span></>
                      ) : (
                        <><CalendarClock className="w-4 h-4 mr-1" /><span className="text-xs">Makeup</span></>
                      )}
                    </Button>
                  )}
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center transition',
                    player.status === 'present' ? 'bg-green-500'
                      : player.status === 'late' ? 'bg-yellow-500'
                      : player.status === 'absent' ? 'bg-red-400'
                      : 'bg-muted'
                  )}>
                    {player.status === 'present' && <CheckCircle className="w-5 h-5 text-white" />}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Session Notes */}
      <SessionNotes sessionId={sessionId} />

      {/* Action Buttons */}
      {data.players.length > 0 && (
        <div className="flex gap-3">
          <Button
            onClick={handleMarkAllPresent}
            disabled={markAllPresent.isPending || data.presentCount === data.totalCount}
            className="flex-1"
          >
            {markAllPresent.isPending ? 'Marking...' : 'Mark All Present'}
          </Button>
          <Button variant="outline" onClick={() => navigate(-1)} className="flex-1">
            Done
          </Button>
        </div>
      )}

      {data.players.length === 0 && (
        <Button variant="outline" onClick={() => navigate(-1)} className="w-full">
          Done
        </Button>
      )}

      <AddPlayersSheet
        sessionId={sessionId}
        open={addSheetOpen}
        onOpenChange={setAddSheetOpen}
      />
    </>
  );
}
